/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "WAITING" TAB (the holding pile)       *
 *  File: apps/web/src/pages/bridge/InboxTab.js                    *
 *  What it does: filters (status, type), the list of record       *
 *  cards, and "Approve selected" with one shared reason.          *
 *  Reads: GET /inbox.  Writes: approve-many, only on confirm.     *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE FILTER CHOICES                                 *
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import InboxCard from './InboxCard';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, STATUS_NAMES, KIND_NAMES } from './bridgeWords';
import { LoadState, WhyBox } from './bridgeParts';
import { fetchPile, approveManyRecords } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE FILTER CHOICES                                 *
 *****************************************************************/

// The statuses, in the order a person thinks about them.
const STATUS_CHOICES = ['proposed', 'failed', 'applied', 'rejected'];

// The kinds of record.
const KIND_CHOICES = ['books_item', 'crm_contact'];

/*****************************************************************
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

/* ---- InboxTab({ onChanged }) -----------------------------------
   onChanged() tells the page to refresh its counts.             */
export default function InboxTab({ onChanged }) {
  const [statusFilter, setStatusFilter] = useState('proposed'); // which status to show
  const [kindFilter, setKindFilter] = useState('');             // '' = both kinds
  const [pileRows, setPileRows] = useState([]);                 // the cards
  const [loadState, setLoadState] = useState('loading');        // loading / ready / error
  const [selectedIds, setSelectedIds] = useState([]);           // ticked cards
  const [isBulkOpen, setIsBulkOpen] = useState(false);          // the shared why box
  const [isBulkBusy, setIsBulkBusy] = useState(false);
  const [bulkAnswer, setBulkAnswer] = useState('');             // what approve-many did

  /* ---- loadPile(isQuiet) -- read the pile for the current filters.
     isQuiet = true keeps the cards on screen while it reloads. ---- */
  const loadPile = useCallback(async (isQuiet) => {
    if (!isQuiet) {
      setLoadState('loading');
    }

    const answer = await fetchPile(statusFilter, kindFilter);

    if (!answer.ok) {
      setLoadState('error');
      return;
    }

    setPileRows(Array.isArray(answer.data) ? answer.data : []);
    setSelectedIds([]);
    setLoadState('ready');
  }, [statusFilter, kindFilter]);

  useEffect(() => {
    loadPile(false);
  }, [loadPile]);

  /* ---- afterDecision() -- reload the pile and the page counts ---- */
  function afterDecision() {
    loadPile(true);
    onChanged();
  }

  /* ---- toggleOne(id) -- tick / untick one card ---- */
  function toggleOne(recordId) {
    const isTicked = selectedIds.includes(recordId);

    if (isTicked) {
      setSelectedIds(selectedIds.filter((oneId) => oneId !== recordId));
    } else {
      setSelectedIds(selectedIds.concat(recordId));
    }
  }

  /* ---- tickAllDecidable() -- tick every card that can be decided ---- */
  function tickAllDecidable() {
    const decidableIds = pileRows
      .filter((oneRow) => oneRow.status === 'proposed' || oneRow.status === 'failed')
      .map((oneRow) => oneRow.id);

    setSelectedIds(decidableIds);
  }

  /* ---- approveTheTicked(whyText) -- approve-many, then say what happened ---- */
  async function approveTheTicked(whyText) {
    setIsBulkBusy(true);

    const answer = await approveManyRecords(selectedIds, whyText);

    setIsBulkBusy(false);
    setIsBulkOpen(false);

    if (!answer.ok) {
      setBulkAnswer(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    const counts = answer.data.counts;
    setBulkAnswer(
      counts.applied + ' put into Butler, '
      + counts.failed + ' need a person, '
      + counts.skipped + ' skipped (already decided).'
    );
    afterDecision();
  }

  return (
    <section aria-labelledby="zb-tab-inbox-title">
      <h2 id="zb-tab-inbox-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabInbox}</h2>

      <div className="zb-filters">
        <label className="zb-field">
          <span className="zb-label">{BRIDGE_WORDS.filterStatus}</span>
          <select className="zb-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUS_CHOICES.map((oneStatus) => (
              <option key={oneStatus} value={oneStatus}>{STATUS_NAMES[oneStatus]}</option>
            ))}
          </select>
        </label>
        <label className="zb-field">
          <span className="zb-label">{BRIDGE_WORDS.filterKind}</span>
          <select className="zb-select" value={kindFilter} onChange={(event) => setKindFilter(event.target.value)}>
            <option value="">{BRIDGE_WORDS.filterAllKinds}</option>
            {KIND_CHOICES.map((oneKind) => (
              <option key={oneKind} value={oneKind}>{KIND_NAMES[oneKind]}</option>
            ))}
          </select>
        </label>
      </div>

      {(statusFilter === 'proposed' || statusFilter === 'failed') && pileRows.length > 0 ? (
        <div className="zb-bulkbar">
          <button type="button" className="zb-button zb-button-plain" onClick={tickAllDecidable}>
            {BRIDGE_WORDS.selectAll}
          </button>
          <span className="zb-small">{selectedIds.length} {BRIDGE_WORDS.selectedCount}</span>
          <button
            type="button"
            className="zb-button zb-button-primary"
            disabled={selectedIds.length === 0}
            onClick={() => setIsBulkOpen(true)}
          >
            <BridgeIcon name="check" />
            <span>{BRIDGE_WORDS.approveSelected}</span>
          </button>
        </div>
      ) : null}

      {isBulkOpen ? (
        <WhyBox
          confirmWord={BRIDGE_WORDS.confirmApprove + ' (' + selectedIds.length + ')'}
          tone="primary"
          busy={isBulkBusy}
          onConfirm={approveTheTicked}
          onCancel={() => setIsBulkOpen(false)}
        />
      ) : null}

      {bulkAnswer ? <div className="zb-notice zb-notice-info" role="status"><span>{bulkAnswer}</span></div> : null}

      {loadState !== 'ready' ? (
        <LoadState state={loadState} onRetry={() => loadPile(false)} />
      ) : null}

      {loadState === 'ready' && pileRows.length === 0 ? (
        <LoadState state="empty" emptyWord={BRIDGE_WORDS.emptyPile} />
      ) : null}

      {loadState === 'ready' ? (
        <div className="zb-cards">
          {pileRows.map((oneRow) => (
            <InboxCard
              key={oneRow.id}
              record={oneRow}
              isSelected={selectedIds.includes(oneRow.id)}
              onToggleSelect={toggleOne}
              onDecided={afterDecision}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
